import { useEffect, useState } from 'react';
import { staffApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useScanMode } from '@/hooks/useScanMode';
import { toast } from 'sonner';
import { Copy, Check, Camera, ScanLine } from 'lucide-react';

export default function Settings() {
  const [logs, setLogs] = useState([]);
  const [copiedKey, setCopiedKey] = useState('');
  const [scanMode, setScanMode] = useScanMode();

  const links = [
    { key: 'login', label: 'Customer Login', url: `${window.location.origin}/cxlogin` },
    { key: 'pos', label: 'POS Portal', url: `${window.location.origin}/pos` },
    { key: 'staffportal', label: 'Staff Portal (Sign-in)', url: `${window.location.origin}/portal/login` },
    { key: 'timeclock', label: 'Time Clock (Punch In/Out)', url: `${window.location.origin}/timeclock` },
  ];

  useEffect(() => {
    staffApi.get('/audit-logs').then(({ data }) => setLogs(data.logs));
  }, []);

  const copyLink = async (key, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      toast.success('Link copied');
      setTimeout(() => setCopiedKey(''), 2000);
    } catch {
      toast.error('Could not copy — select and copy the link manually');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-700 text-charcoal">Settings</h1>

      <div className="bg-white rounded-xl border border-border/40 p-5 space-y-2 max-w-md">
        <h2 className="font-heading font-semibold text-charcoal">Business Information</h2>
        <p className="text-sm text-muted-ink">THE TIFFIN — Daily Fresh. Daily Different.</p>
        <p className="text-sm text-muted-ink">Edmonton, Alberta, Canada</p>
        <p className="text-xs text-muted-ink pt-2">
          More configurable business settings (tax rate, receipt footer, etc.) can be added here as they're confirmed.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-border/40 p-5 space-y-3 max-w-md">
        <div>
          <h2 className="font-heading font-semibold text-charcoal">Default Scan Mode</h2>
          <p className="text-sm text-muted-ink mt-1">
            Applies to this device only (QR Scanner and POS). Whichever mode is picked stays selected
            until changed again — no more clicking into a box before every scan.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="settings-scan-mode-camera-btn"
            onClick={() => setScanMode('CAMERA')}
            className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-full text-sm font-medium transition-colors duration-150 ${
              scanMode === 'CAMERA' ? 'bg-saffron text-white' : 'bg-bone text-muted-ink hover:bg-bone/70'
            }`}
          >
            <Camera size={14} /> Camera
          </button>
          <button
            data-testid="settings-scan-mode-scanner-btn"
            onClick={() => setScanMode('SCANNER')}
            className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-full text-sm font-medium transition-colors duration-150 ${
              scanMode === 'SCANNER' ? 'bg-saffron text-white' : 'bg-bone text-muted-ink hover:bg-bone/70'
            }`}
          >
            <ScanLine size={14} /> Handheld Scanner
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border/40 p-5 space-y-4 max-w-md">
        <div>
          <h2 className="font-heading font-semibold text-charcoal">Portal Links</h2>
          <p className="text-sm text-muted-ink mt-1">
            Share the Customer Login on your website or social media — customers can log in, create an
            account, or install THE TIFFIN as an app on their phone. The POS Portal link is for staff
            devices at the counter.
          </p>
        </div>
        {links.map((l) => (
          <div key={l.key} className="space-y-1.5">
            <p className="text-xs font-medium text-muted-ink">{l.label}</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                data-testid={`${l.key}-link-input`}
                value={l.url}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 h-10 rounded-lg border border-border/40 px-3 text-sm text-charcoal bg-bone"
              />
              <Button
                variant="outline"
                size="sm"
                data-testid={`copy-${l.key}-link-btn`}
                onClick={() => copyLink(l.key, l.url)}
                className="shrink-0 rounded-full border-saffron text-saffron hover:bg-saffron/10 transition-colors duration-150"
              >
                {copiedKey === l.key ? <Check size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
                {copiedKey === l.key ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-border/40">
        <h2 className="font-heading font-semibold text-charcoal p-4 pb-0">Audit History</h2>
        <div className="divide-y divide-border/30">
          {logs.map((l) => (
            <div key={l.id} className="p-3 px-4 flex flex-wrap gap-x-3 gap-y-1 text-sm" data-testid="audit-log-row">
              <span className="text-muted-ink">{new Date(l.createdAt).toLocaleString('en-CA')}</span>
              <span className="text-charcoal">{l.action}</span>
              <span className="text-muted-ink">{l.staff?.name || 'System'}</span>
              <span className="text-muted-ink">{l.reason || '—'}</span>
            </div>
          ))}
          {logs.length === 0 && <p className="p-4 text-sm text-muted-ink">No audit history yet.</p>}
        </div>
      </div>
    </div>
  );
}
